// Ad-related TypeScript type definitions

// Basic ad size configuration
export type AdSize = [number, number];

// Media types for ad units
export interface MediaTypes {
  banner?: {
    sizes: AdSize[];
  };
  native?: {
    title?: { required?: boolean; len?: number };
    body?: { required?: boolean; len?: number };
    image?: { required?: boolean; sizes?: AdSize[] };
    sponsoredBy?: { required?: boolean; len?: number };
  };
  video?: {
    context: 'instream' | 'outstream';
    playerSize: AdSize[];
    mimes: string[];
    protocols: number[];
    minduration?: number;
    maxduration?: number;
  };
}

// Bidder configuration for Prebid.js
export interface BidderConfig {
  bidder: string;
  params: Record<string, any>;
}

// Targeting data for contextual advertising
export interface TargetingData {
  keywords?: string[];
  categories?: string[];
  demographics?: TargetingData;
  behavioral?: BehavioralData;
  contextual?: ContextualData;
  device_type?: string;
  viewport_width?: string;
  viewport_height?: string;
  touch_enabled?: string;
  [key: string]: any; // Allow additional targeting parameters
}

export interface TargetingData {
  ageRange?: string;
  gender?: string;
  location?: string;
}

export interface BehavioralData {
  interests?: string[];
  recentSearches?: string[];
  engagementLevel?: 'low' | 'medium' | 'high';
}

export interface ContextualData {
  pageType?: 'home' | 'outfit-detail' | 'search' | 'profile' | 'upload';
  fashionStyles?: string[];
  brands?: string[];
  events?: string[];
  season?: string;
}

// Ad unit configuration
export interface AdUnit {
  code: string;
  mediaTypes: MediaTypes;
  bids: BidderConfig[];
  targeting?: TargetingData;
  sizes: AdSize[];
}

// Ad slot configuration
export interface AdSlotConfig {
  slotId: string;
  sizes: AdSize[];
  targeting?: TargetingData;
  refreshRate?: number;
  lazy?: boolean;
  priority?: number;
}

// Prebid.js configuration
export interface PrebidConfig {
  timeout: number;
  bidders: BidderConfig[];
  priceGranularity: 'low' | 'medium' | 'high' | 'auto' | 'dense' | Record<string, any>;
  enableSendAllBids?: boolean;
  useBidCache?: boolean;
  enableHeaderBidding?: boolean;
}

// Google Ad Manager configuration
export interface GAMConfig {
  networkId: string;
  enableSRA?: boolean; // Single Request Architecture
  collapseEmptyDivs?: boolean;
  enableLazyLoad?: boolean;
  lazyLoadConfig?: {
    fetchMarginPercent: number;
    renderMarginPercent: number;
    mobileScaling: number;
  };
  enableHeaderBidding?: boolean;
  headerBiddingTimeout?: number;
}

// Complete ad configuration
export interface AdConfig {
  slots: Record<string, AdSlotConfig>;
  prebid: PrebidConfig;
  gam: GAMConfig;
  analytics?: {
    enabled: boolean;
    providers?: string[];
  };
}

// Ad performance metrics
export interface AdMetrics {
  rpm: number; // Revenue per mille
  fillRate: number; // Percentage of ad requests filled
  ctr: number; // Click-through rate
  viewability: number; // Viewability percentage
  loadTime: number; // Ad load time in milliseconds
  revenue: number; // Total revenue
}

// Ad analytics data
export interface AdAnalyticsData {
  slotId: string;
  timestamp: number;
  metrics: AdMetrics;
  targeting: TargetingData;
  userAgent: string;
  viewport: {
    width: number;
    height: number;
  };
}

// Ad error types
export enum AdErrorType {
  TIMEOUT = 'timeout',
  NO_FILL = 'no_fill',
  INVALID_CONFIG = 'invalid_config',
  NETWORK_ERROR = 'network_error',
  BLOCKED = 'blocked',
  SCRIPT_LOAD_ERROR = 'script_load_error'
}

// Ad error interface
export interface AdError {
  type: AdErrorType;
  slotId: string;
  message: string;
  timestamp: Date;
  context?: any;
}

export interface ImpressionData {
  filled: boolean;
  viewable: boolean;
  loadTime?: number;
  revenue?: number;
  targeting?: Record<string, any>;
  advertiser?: string;
  campaign?: string;
  pwaEnabled?: boolean;
  offline?: boolean;
  fallback?: boolean;
  device?: 'mobile' | 'desktop';
  touchEnabled?: boolean;
  timestamp?: number;
}

export interface ClickData {
  revenue?: number;
  advertiser?: string;
  campaign?: string;
  clickUrl?: string;
  timestamp?: number;
}

export interface TimeRange {
  days: number;
}

export interface AnalyticsReport {
  timeRange: TimeRange;
  totalImpressions: number;
  totalClicks: number;
  totalRevenue: number;
  averageRPM: number;
  averageCTR: number;
  averageFillRate: number;
  averageViewability: number;
  slotPerformance: Record<string, AdMetrics>;
  topPerformingSlots: Array<{ slotId: string, metrics: AdMetrics }>;
  revenueByDay: Array<{ date: string, revenue: number }>;
  errors: Array<{ type: string, count: number }>;
}

export interface ABTestConfig {
  testId: string;
  variants: {
    control: AdConfiguration;
    treatment: AdConfiguration;
  };
  trafficSplit: number;
  metrics: string[];
  duration: number;
}

export interface ABTest {
  testId: string;
  variants: {
    control: AdConfiguration;
    treatment: AdConfiguration;
  };
  trafficSplit: number;
  metrics: string[];
  duration: number;
  status: 'active' | 'paused' | 'completed';
  startDate: Date;
  results: Map<string, {
    impressions: number;
    clicks: number;
    revenue: number;
    metrics: Record<string, any>;
  }>;
}

export interface TestResults {
  testId: string;
  status: 'significant' | 'not_significant' | 'insufficient_data';
  confidence: number;
  winner: 'control' | 'treatment' | null;
  improvement?: number;
  metrics: {
    control?: {
      ctr: number;
      rpm: number;
      impressions: number;
      clicks: number;
    };
    treatment?: {
      ctr: number;
      rpm: number;
      impressions: number;
      clicks: number;
    };
  };
}

export interface AdConfiguration {
  slotId: string;
  sizes: AdSize[];
  targeting?: TargetingData;
  refreshRate?: number;
  lazy?: boolean;
  priority?: number;
}

// User profile for ad targeting
export interface UserProfile {
  userId: string;
  demographics: TargetingData;
  interests: {
    fashionStyles: string[];
    brands: string[];
    events: string[];
    travelDestinations: string[];
  };
  behavior: {
    avgSessionTime: number;
    pagesPerSession: number;
    preferredDevices: string[];
    activeHours: number[];
  };
  lastUpdated: Date;
}

// Revenue tracking data
export interface RevenueData {
  date: string;
  slotId: string;
  impressions: number;
  clicks: number;
  revenue: number;
  rpm: number;
  fillRate: number;
  advertiser?: string;
  campaign?: string;
}



// Seasonal targeting data
export interface SeasonalData {
  season: 'spring' | 'summer' | 'fall' | 'winter';
  holidays: string[];
  events: string[];
  trends: string[];
}

// Event-based targeting
export interface EventData {
  eventType: string;
  eventDate?: Date;
  location?: string;
  dresscode?: string;
  weather?: string;
}

// Outfit data for contextual targeting
export interface OutfitData {
  id: string;
  userId: string;
  imageUrl: string;
  description?: string;
  tags: string[];
  brands: string[];
  categories: string[];
  event?: string;
  season?: string;
  aiAnalysis?: {
    clothingItems: string[];
    style: string;
    colors: string[];
    occasion: string;
  };
}

// Ad slot component props
export interface AdSlotProps {
  slotId: string;
  sizes: AdSize[];
  targeting?: TargetingData;
  className?: string;
  lazy?: boolean;
  refreshInterval?: number;
  onLoad?: () => void;
  onError?: (error: AdError) => void;
}

// Ad slot state
export interface AdSlotState {
  isLoaded: boolean;
  isVisible: boolean;
  error?: AdError;
  revenue?: number;
}

// Global interface extensions for ad libraries are now in types/global.d.ts